import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import "card-pattern/dist/main.css";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import { TableColumn, TableRow } from "../../../components/common/Table/Table";
import { type DropdownOption } from "../../../components/common/Dropdown";
import { useNavigate , Link } from "react-router-dom";
import {
  fetchProjectList,
  fetchSearchByOptions,
  fetchSearchingClosedAndOpenedProjects,
  fetchCostCenters,
  fetchProjectRegion,
  fetchProjectArea,
  fetchProjectCenter,
  fetchProjectLocation,
  fetchProjectCostCenters,
  fetchSearchProjectFocusData,
  fetchSearchProjectMetrics,
  setSelectedRegionCode,
  setSelectedEntityCode,
  setSelectedAreaCode,
  setSelectedCenterCode,
  setSelectedLocationCode,
  setSelectedCostCenterCode,
  setSortByCostCenter,
  setSelectedRating,
  setSelectedValueCompass,
  setSelectedMetrix,
  setSelectedResults,
  setSelectedTestOfChangeRating,
  setSelectedProjectType,
  setSelectedSearchBy,
  setSelectedSearchByText,
  setSelectedFromId,
  setSelectedToId,
  setSelectedProjectName,
  resetDependentDropdowns,
  searchByOptions as defaultSearchByOptions,
} from "../../../store/slices/projectSlice";
import { downloadDisplaySummaryAndDetailsReport, isPdfDownloadLoading } from "../../../store/slices/teamsReportSlice";
import {
  exportTestOfChangeCSV,
  downloadPipDetailsReport,
  selectReportLoading,
  selectReportError,
  clearReportState,
  type TestOfChangeReportParams,
} from "../../../store/slices/reportSlice";
import Loader from "../../../components/common/Loader";
import "form-pattern/dist/main.css";
import SearchProjectContainer from "./SearchProjectContainer";
import UBTSearchResults from "../../../components/common/UBTSearchResults";
import {
  loadSearchFromSession,
  saveSearchToSession,
  type SharedSearchData,
} from "../../../utils/searchSessionStorage";
import {
  saveAdvanceSearch,
  loadAdvanceSearch,
  formatMultiSelectForApi,
  type AdvanceSearchData,
} from "../../../utils/advanceSearchSessionStorage";
import PopUp from "../../../components/common/PopUp/PopUp";
import SearchProjectsHelpModal from "./SearchProjectsHelpModal";
import { teamStorage } from "../../../utils/teamStorage";
import { EXPORT_TEST_OF_CHANGE_ID, EXPORT_TEST_OF_CHANGE_VALUE, EXPORT_TEST_DATA_OPTION_ID, TEAM_SUMMARY_OPTION_ID, TEAM_DETAILS_OPTION_ID } from "../../../utils/reportConstants";
import {
  getSearchUBTProjectsOptions,
  EXPORT_PIP_HISTORY_OPTION_ID,
  EXPORT_PIP_DETAILS_OPTION_ID,
} from "../../../constants/reportDropdownOptions";
import { getCostCenterParam } from "../../../utils/costCenter";
import {
  downloadTestSeriesByCriteriaReport,
  downloadPipHistoryReport,
  selectReportDownloadLoading,
} from "../../../store/slices/teamReportSlice";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import {
  type GroupedProject,
  type ProjectDetail,
} from "../../../utils/projectDataTransformers";

/** Maps UI table column keys to the API sortColumn values expected by fetchProjectList. */
const SEARCH_PROJECTS_SORT_COLUMN_MAP: Record<string, string> = {
  id: 'ubtId',
  UBTname: 'ubtName',
  centerName: 'centerName',
  locationName: 'locationName',
  projectName: 'projectName',
  focusArea: 'focusArea',
  results: 'results',
  valueCompass: 'valueCompassId',
  projectStartDate: 'projectstartDate',
};

const columns: TableColumn[] = [
  { key: "id", label: "ID", isSortable: true },
  { key: "UBTname", label: "UBT Name", className: "md:max-w-[12rem] whitespace-normal break-words", isSortable: true},
  { key: "centerName", label: "Center Name", isSortable: true},
  { key: "locationName", label: "Location Name", isSortable: true},
  { key: "projectName", label: "Name of Project", isSortable: true},
  { key: "focusArea", label: "Focus Area", isSortable: true},
  { key: "results", label: "Results", isSortable: true},
  { key: "valueCompass", label: "Value Compass", isSortable: true},
  { key: "projectStartDate", label: "Project Start", isSortable: true },
];

const DEFAULT_REGION_OPTION = {
  id: "02",
  label: "Northern California",
  value: "02",
};

const DEFAULT_SEARCH_BY_OPTION: DropdownOption = {
  id: 0,
  label: "UBT Name",
  value: "UBTName",
};

/**
 * Resolves a shared Search By selection to an option supported by Search Projects.
 *
 * The shared session is also used by Find and Select, so the selected option
 * must be resolved against the options currently supported by this page.
 */
export const resolveSearchByOption = (
  storedOption: DropdownOption | undefined,
  availableOptions: DropdownOption[] = [],
): DropdownOption => {
  const supportedOptions = availableOptions.length > 0
    ? availableOptions
    : defaultSearchByOptions;

  return supportedOptions.find((option) => option.value === storedOption?.value)
    ?? supportedOptions[0]
    ?? DEFAULT_SEARCH_BY_OPTION;
};

/**
 * Adds a multi-select field to test-series params only when specific items are
 * selected (i.e. not "All"). Module-level to keep useCallback complexity low.
 */
const addTestSeriesMultiSelect = (
  params: Record<string, string>,
  field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined,
  key: string,
): void => {
  if (field && !field.isAllSelected && field.selectedIds?.length) {
    params[key] = field.selectedIds.join(",");
  }
};

/**
 * Routes search-text to the appropriate API param key based on the
 * selected search-by type. Module-level to keep useCallback complexity low.
 */
const addSearchTextParam = (
  params: Record<string, string>,
  searchText: string | undefined,
  searchBy: string | undefined,
): void => {
  if (!searchText) return;
  if (searchBy === "CostCenterName") {
    params.costCenterName = searchText;
  } else if (searchBy === "ProjectName") {
    params.projectName = searchText;
  } else if (searchBy === "UBTId") {
    params.ubtId = searchText;
  } else {
    params.ubtName = searchText;
  }
};

/**
 * Helper function to extract the option code/ID from a dropdown option.
 * Returns the ID if it exists and is not '0', otherwise returns the value.
 * Falls back to 'All' if both are empty.
 */
const getOptionCode = (option?: DropdownOption | null): string => {
  if (!option) return "All";
  const idValue = String(option.id ?? "").trim();
  if (idValue && idValue !== "0") {
    return idValue;
  }
  const optionValue = String(option.value ?? "").trim();
  return optionValue || "All";
};

/**
 * Builds query parameters for the `/ubt/reports/pip-details` API from session
 * storage (SearchUBTProjects page). All filter values default to `"All"` when
 * not explicitly set, matching the API contract.
 *
 * Exported for direct unit testing.
 *
 * @param sortColumn - Current table sort column forwarded to the API.
 * @param sortOrder - Current table sort direction forwarded to the API.
 * @returns A record of string query params keyed to the API's expected names.
 */
export const buildPipDetailsParams = (
  sortColumn: string,
  sortOrder: string,
): Record<string, string> => {
  const searchData = loadSearchFromSession();
  const advanceData = loadAdvanceSearch();

  const getMultiSelectValue = (
    field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined,
  ): string => {
    if (!field || field.isAllSelected || !field.selectedIds?.length) return "All";
    return field.selectedIds.join(",");
  };

  const regionCode = getOptionCode(searchData?.selectedRegionCode);
  const entityCode = getOptionCode(searchData?.selectedEntityCode);
  const areaCode = getOptionCode(searchData?.selectedAreaCode);
  const serviceAreaCode = getOptionCode(searchData?.selectedCenterCode);
  const locationCode = getOptionCode(searchData?.selectedLocationCode);
  const deptIds = getCostCenterParam(searchData?.selectedCostCenterCode, ",") || "All";

  const focusAreas = getMultiSelectValue(advanceData?.selectedFocusArea);
  const valueCompassIds = getMultiSelectValue(advanceData?.selectedValueCompass);
  const metrics = getMultiSelectValue(advanceData?.selectedMetrics);
  const resultIds = getMultiSelectValue(advanceData?.selectedResults);
  const tocRatingIds = getMultiSelectValue(advanceData?.selectedTestOfChangeRating);

  const projectTypeMapping: Record<string, string> = {
    active: "active",
    Active: "active",
    complete: "complete",
    Complete: "complete",
    both: "both",
    Both: "both",
  };
  const rawType = advanceData?.selectedProjectType ?? "active";
  const perfStatus = projectTypeMapping[rawType] ?? "active";

  // Convert MM-DD-YYYY (session storage) to MM/DD/YYYY (API payload)
  const toApiDate = (d: string): string =>
    d.replace(/^(\d{2})-(\d{2})-(\d{4})$/, "$1/$2/$3");

  const params: Record<string, string> = {
    regionCode,
    entityCode,
    areaCode,
    serviceAreaCode,
    locationCode,
    deptIds,
    focusAreas,
    valueCompassIds,
    metrics,
    resultIds,
    tocRatingIds,
    perfStatus,
    sortColumn,
    sortOrder,
  };

  // Optional date / ROI filters — only added when present
  if (advanceData?.rangeStartDate) params.startDate = toApiDate(advanceData.rangeStartDate);
  if (advanceData?.rangeEndDate) params.endDate = toApiDate(advanceData.rangeEndDate);
  if (advanceData?.minROI) params.minRoi = advanceData.minROI;
  if (advanceData?.maxROI) params.maxRoi = advanceData.maxROI;

  // Project name search
  if (searchData?.selectedProjectName) params.searchByProject = searchData.selectedProjectName;

  // Search criterion and entered value, matching Team Details report payloads.
  if (searchData?.selectedSearchBy?.value) {
    params.searchBy = searchData.selectedSearchBy.value;
  }
  if (searchData?.selectedSearchByText) {
    params.searchString = searchData.selectedSearchByText;
  }

  // Cost center number — only when the search-by type is explicitly CostCenterNumber
  if (
    searchData?.selectedSearchBy?.value === "CostCenterNumber" &&
    searchData.selectedSearchByText
  ) {
    params.costCenter = searchData.selectedSearchByText;
  }

  addUbtIdRange(params, searchData);

  return params;
};

/**
 * Returns a warning message when the requested report exceeds the project count limit.
 * Extracted at module level to reduce cognitive complexity of handleReportExport.
 * @param reportId - The selected report option ID.
 * @param count - The current total project count.
 * @returns A non-empty warning string if the limit is exceeded, otherwise an empty string.
 */
const getTeamReportWarning = (reportId: string | number | undefined, count: number): string => {
  if (reportId === TEAM_SUMMARY_OPTION_ID && count > 250)
    return "Team Summary Report is not Available for more than Max Projects Size 250";
  if (reportId === TEAM_DETAILS_OPTION_ID && count > 250)
    return "Team Detail Report is not Available for more than Max Projects Size 250";
  return "";
};

// ── Module-level pure helpers (exported for unit testing) ─────────────────────

/** Converts MM-DD-YYYY (session storage) → MM/DD/YYYY (API payload). */
const toApiDateFormat = (d: string): string =>
  d.replace(/^(\d{2})-(\d{2})-(\d{4})$/, "$1/$2/$3");

/** Maps selectedProjectType string to the lowercase API value. */
const deriveProjectType = (projectType?: string): string => {
  if (projectType === "Active" || projectType === "active") return "active";
  if (projectType === "Complete" || projectType === "complete") return "complete";
  if (projectType === "Both" || projectType === "both") return "both";
  return "active";
};

/** Adds a single-select field to params when the value is non-empty. */
const addSingleSelectValue = (
  params: Record<string, string>,
  value: string | number | undefined,
  key: string,
): void => {
  if (value) params[key] = String(value);
};

/** Adds the active UBT ID range only for UBT ID# report searches. */
const addUbtIdRange = (
  params: Record<string, string>,
  searchData: SharedSearchData | null,
): void => {
  if (searchData?.selectedSearchBy?.value !== "UBTId") return;
  if (searchData.selectedFromId) params.fromId = searchData.selectedFromId;
  if (searchData.selectedToId) params.toId = searchData.selectedToId;
};

/** Adds a multi-select field (All or comma-joined IDs) to params. */
const addMultiSelectValue = (
  params: Record<string, string>,
  field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined,
  key: string,
): void => {
  if (field?.isAllSelected) {
    params[key] = "All";
  } else if (field?.selectedIds?.length) {
    const ids = key === "costCenterCd"
      ? field.selectedIds.map((id) => String(id).split("-")[0]).filter(Boolean)
      : field.selectedIds;
    params[key] = ids.join(",");
  }
};

/** Adds a cost-centre field (stripping label prefix) to params. */
const addCostCenterValue = (
  params: Record<string, string>,
  field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined,
  key: string,
  separator: "," | "~" = ",",
): void => {
  const value = getCostCenterParam(field, separator);
  if (value) params[key] = value;
};

/**
 * Builds test-of-change CSV report params from session storage.
 * Exported for direct unit testing; the in-component hook wraps this.
 *
 * @param sortDirection - Current table sort direction (used for sortOrder).
 */
export const buildTestOfChangeParamsFromSession = (
  sortDirection: string,
): TestOfChangeReportParams | null => {
  const searchData = loadSearchFromSession();
  const advanceData = loadAdvanceSearch();
  if (!searchData) return null;

  const tocParams: Record<string, string> = {
    reportIdentifier: "testofchange",
    pageId: "ubtReportIdSearchProjects",
    sortColumn: "ubtId",
    sortOrder: sortDirection.toLowerCase(),
  };

  addSingleSelectValue(tocParams, searchData.selectedRegionCode?.value, "regionCd");
  addSingleSelectValue(tocParams, searchData.selectedEntityCode?.value, "entityCd");
  addSingleSelectValue(tocParams, searchData.selectedAreaCode?.value, "areaCd");
  addSingleSelectValue(tocParams, searchData.selectedCenterCode?.value, "centerCd");
  addSingleSelectValue(tocParams, searchData.selectedLocationCode?.value, "locationCd");
  addSingleSelectValue(tocParams, searchData.selectedSearchBy?.value, "queryBy");
  addSingleSelectValue(tocParams, searchData.selectedSearchByText, "searchString");
  addSingleSelectValue(tocParams, searchData.selectedProjectName, "searchProjectString");
  addUbtIdRange(tocParams, searchData);


  addCostCenterValue(tocParams, searchData.selectedCostCenterCode, "costCenterCd", ",");

  addMultiSelectValue(tocParams, advanceData?.selectedFocusArea, "focusAreaCd");
  addMultiSelectValue(tocParams, advanceData?.selectedValueCompass, "valueCompassStaticCd");
  addMultiSelectValue(tocParams, advanceData?.selectedMetrics, "metricCd");
  addMultiSelectValue(tocParams, advanceData?.selectedResults, "resultCd");
  addMultiSelectValue(tocParams, advanceData?.selectedTestOfChangeRating, "tocRatingCd");

  tocParams.includeProjectType = deriveProjectType(advanceData?.selectedProjectType);

  if (advanceData?.rangeStartDate) tocParams.startDate = toApiDateFormat(advanceData.rangeStartDate);
  if (advanceData?.rangeEndDate) tocParams.endDate = toApiDateFormat(advanceData.rangeEndDate);
  if (advanceData?.minROI) tocParams.minRoi = advanceData.minROI;
  if (advanceData?.maxROI) tocParams.maxRoi = advanceData.maxROI;

  return tocParams;
};

/**
 * Builds test-series CSV report params from session storage.
 * Exported for direct unit testing; the in-component hook wraps this.
 *
 * @param sortDirection - Current table sort direction.
 */
export const buildTestSeriesParamsFromSession = (
  sortDirection: string,
): Record<string, string> | null => {
  const searchData = loadSearchFromSession();
  const advanceData = loadAdvanceSearch();
  if (!searchData) return null;

  const params: Record<string, string> = {
    sortColumn: "teamId",
    sortOrder: sortDirection,
  };

  if (searchData.selectedRegionCode?.value) params.regionCode = searchData.selectedRegionCode.value;

  const areaId = searchData.selectedAreaCode?.id;
  if (areaId && areaId !== 0) params.areaCode = String(areaId);

  const centerId = searchData.selectedCenterCode?.id;
  if (centerId && centerId !== 0) params.serviceAreaCode = String(centerId);

  const locationId = searchData.selectedLocationCode?.id;
  if (locationId && locationId !== 0) params.locationCode = String(locationId);

  const entityId = searchData.selectedEntityCode?.id;
  if (entityId && entityId !== 0) params.companyCd = String(entityId);

  const deptIds = getCostCenterParam(searchData.selectedCostCenterCode, ",");
  if (deptIds && deptIds !== "All") params.deptIds = deptIds;

  addTestSeriesMultiSelect(params, advanceData?.selectedFocusArea, "focusAreas");
  addTestSeriesMultiSelect(params, advanceData?.selectedValueCompass, "valueCompassIds");
  addTestSeriesMultiSelect(params, advanceData?.selectedMetrics, "metrics");
  addTestSeriesMultiSelect(params, advanceData?.selectedResults, "results");
  addTestSeriesMultiSelect(params, advanceData?.selectedTestOfChangeRating, "tocRatings");

  addSingleSelectValue(params, searchData.selectedSearchBy?.value, "searchBy");
  addSingleSelectValue(params, searchData.selectedSearchByText, "searchString");
  addSearchTextParam(params, searchData.selectedSearchByText, searchData.selectedSearchBy?.value);
  addUbtIdRange(params, searchData);


  // Only append the project-name filter when a project name is present. Uses the
  // dedicated `selectedProjectName` field to stay consistent with the other report
  // exports (PIP History / PIP Details / Test of Change).
  if (searchData.selectedProjectName) params.projectName = searchData.selectedProjectName;

  if (advanceData?.selectedProjectType) params.perfStatus = advanceData.selectedProjectType;

  return params;
};

const SearchProject: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    projectList = [],
    searchByOptions,
    rating,
    ratingOption,
    costCenters,
    results,
    valueCompass,
    testOfChangeRating,
    projectRegion,
    projectEntity,
    projectArea,
    projectCenter,
    projectLocation,
    selectedRegionCode,
    selectedEntityCode,
    selectedAreaCode,
    selectedCenterCode,
    selectedLocationCode,
    selectedCostCenterCode,
    sortByCostCenter,
    selectedRating,
    selectedValueCompass,
    selectedMetrix,
    selectedResults,
    selectedTestOfChangeRating,
    selectedProjectType,
    selectedSearchBy,
    selectedSearchByText,
    selectedFromId,
    selectedToId,
    selectedProjectName,
    loading,
    loadingDropdowns,
    perPage,
    currentPage,
    totalItems,
  } = useAppSelector((state) => state.project);
  const profile = useAppSelector((state) => state.profile?.profile);
  const pdfDownloadLoading = useAppSelector(isPdfDownloadLoading);
  const tocExportLoading = useAppSelector(selectReportLoading);
  const tocExportError = useAppSelector(selectReportError);
  const pipHistoryLoading = useAppSelector(selectReportDownloadLoading);

  const profileRegionOption = useMemo(() => {
    const regionCode =
      profile?.currentRegion ||
      profile?.primaryRegion ||
      DEFAULT_REGION_OPTION.value;
    const regionDescription =
      profile?.regionDescription || DEFAULT_REGION_OPTION.label;

    return {
      id: regionCode,
      label: regionDescription,
      value: regionCode,
    };
  }, [profile]);

  const effectiveSelectedRegionCode = useMemo(() => {
    /**
     * Fall back to the profile's region ONLY when the Redux slice has no
     * explicit selection yet (value is absent, empty, or the literal
     * placeholder string "Select").
     *
     * IMPORTANT: Do NOT match on the label/value of DEFAULT_REGION_OPTION.
     * That check causes Northern California (value "02") to be treated as
     * "unset", so every time the user picks that region the dropdown
     * immediately reverts back to profileRegionOption — making it look like
     * the selection has no effect.
     */
    const isUnset =
      !selectedRegionCode?.value ||
      selectedRegionCode.value === "" ||
      selectedRegionCode.value === "Select";

    return isUnset ? profileRegionOption : selectedRegionCode;
  }, [profileRegionOption, selectedRegionCode]);

  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<string>("id");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");
  const [showResults, setShowResults] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState<Record<string, unknown>>({});
  const [sessionRestored, setSessionRestored] = useState(false);
  // True from first render until the initial data load cycle (dropdowns + project
  // list) fully completes. Keeps the loader visible as one uninterrupted span so
  // it does not flicker off between the dropdown-load phase and the project-list
  // fetch phase.
  const [isPageInitializing, setIsPageInitializing] = useState(true);
  const [showTocExportErrorBanner, setShowTocExportErrorBanner] = useState<boolean>(false);
  const [exportWarningMessage, setExportWarningMessage] = useState<string>("");

  // Restored date/ROI values from ubt-advance-search session storage.
  // Passed as initial-value props to SearchProjectContainer so they populate
  // the date-pickers and ROI inputs on page load without lifting all that
  // local state into this parent.
  const [restoredRangeStartDate, setRestoredRangeStartDate] = useState("");
  const [restoredRangeEndDate, setRestoredRangeEndDate] = useState("");
  const [restoredMinROI, setRestoredMinROI] = useState("");
  const [restoredMaxROI, setRestoredMaxROI] = useState("");
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  // Open help modal when the popup's "additional page help" anchor is clicked
  useEffect(() => {
    const handleHelpClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-open-modal="search-projects-help"]')) {
        e.preventDefault();
        setIsHelpModalOpen(true);
      }
    };
    document.addEventListener("click", handleHelpClick);
    return () => document.removeEventListener("click", handleHelpClick);
  }, []);

  // Ref to prevent duplicate API calls for Focus Area changes
  const lastFocusAreaCallRef = useRef<string>("");

  // Ref to save scroll position during loading to prevent page jump
  const savedScrollPosition = useRef<number>(0);
  const isSearchScrolling = useRef<boolean>(false);

  // Ref for scrolling to results table after search
  const resultsTableRef = useRef<HTMLDivElement>(null);
  // Track if we should scroll after auto-population from session
  const shouldScrollAfterSession = useRef<boolean>(false);
  // Ref for scrolling to the export warning banner
  const exportWarningRef = useRef<HTMLDivElement>(null);

  // Guard: ensures the initialization useEffect body executes exactly once.
  // profileRegionOption is a useMemo that creates a new object reference whenever
  // profile changes (null → loaded), causing the effect to fire twice without this guard.
  const hasRunInitializationRef = useRef(false);

  // Guard: prevents the session-restoration async loadData() from starting a
  // second concurrent run if projectRegion gets a new reference before the
  // first run completes and sets sessionRestored = true.
  const sessionLoadInProgressRef = useRef(false);

  // Transitions isPageInitializing → false once the first full data-load cycle
  // completes: initialization has run, session restoration is done, and nothing
  // is still loading. All three must be true simultaneously so the loader never
  // disappears between the dropdown phase and the project-list fetch phase.
  useEffect(() => {
    if (
      isPageInitializing &&
      hasRunInitializationRef.current &&
      sessionRestored &&
      !loading &&
      !loadingDropdowns
    ) {
      setIsPageInitializing(false);
    }
  }, [isPageInitializing, loading, loadingDropdowns, sessionRestored]);

  const scrollToTable = useCallback(() => {
    if (resultsTableRef.current) {
      const tableTop =
        resultsTableRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: tableTop - 20, behavior: "smooth" });
    }
  }, []);

  /**
   * Converts MM/DD/YYYY date string to YYYY-MM-DD format for API.
   */
  const convertDateToApiFormat = useCallback((dateStr: string): string => {
    // Convert MM-DD-YYYY (session storage format) to MM/DD/YYYY (API payload format)
    return dateStr.replace(/^(\d{2})-(\d{2})-(\d{4})$/, "$1/$2/$3");
  }, []);

  /**
   * Formats cost center selection for API payload.
   * Returns "All", empty string, or delimiter-separated code prefixes
   * (everything before the first hyphen in each label).
   * Delegates to the shared {@link getCostCenterParam} utility.
   */
  const formatCostCenterForApi = useCallback(
    (
      selectedCostCenter: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined,
      separator: "," | "~" = ",",
    ): string => getCostCenterParam(selectedCostCenter, separator),
    [],
  );

  /**
   * Converts radio button value (Active/Complete/Both) to API format (active/complete/both).
   * Defaults to "active" when no value is provided.
   */
  const deriveProjectTypeForApi = useCallback((projectType?: string): string => {
    if (projectType === "Active") return "active";
    if (projectType === "Complete") return "complete";
    if (projectType === "Both") return "both";
    return "active";
  }, []);

  /**
   * Restores basic search filters to Redux state from session storage.
   */
  const restoreBasicSearchFilters = useCallback((
    storedSearch: SharedSearchData,
    restoredSearchBy: DropdownOption,
    restoreSearchByText: boolean,
  ) => {
    if (storedSearch.selectedRegionCode) {
      dispatch(setSelectedRegionCode(storedSearch.selectedRegionCode));
    }
    if (storedSearch.selectedEntityCode) {
      dispatch(setSelectedEntityCode(storedSearch.selectedEntityCode));
    }
    if (storedSearch.selectedAreaCode) {
      dispatch(setSelectedAreaCode(storedSearch.selectedAreaCode));
    }
    if (storedSearch.selectedCenterCode) {
      dispatch(setSelectedCenterCode(storedSearch.selectedCenterCode));
    }
    if (storedSearch.selectedLocationCode) {
      dispatch(setSelectedLocationCode(storedSearch.selectedLocationCode));
    }
    dispatch(setSelectedSearchBy(restoredSearchBy));
    dispatch(setSelectedSearchByText(
      restoreSearchByText ? storedSearch.selectedSearchByText ?? "" : "",
    ));
    dispatch(setSelectedFromId(
      restoreSearchByText && restoredSearchBy.value === "UBTId"
        ? storedSearch.selectedFromId ?? ""
        : "",
    ));
    dispatch(setSelectedToId(
      restoreSearchByText && restoredSearchBy.value === "UBTId"
        ? storedSearch.selectedToId ?? ""
        : "",
    ));
    if (storedSearch.selectedProjectName !== undefined) {
      dispatch(setSelectedProjectName(storedSearch.selectedProjectName));
    }
  }, [dispatch]);

  /**
   * Restores advanced search filters (multi-select fields, project type, dates, ROI)
   * from ubt-advance-search session storage.
   */
  const restoreAdvancedSearchFilters = useCallback((storedAdvance: AdvanceSearchData | null) => {
    if (!storedAdvance) return;

    if (storedAdvance.selectedFocusArea) {
      dispatch(setSelectedRating(storedAdvance.selectedFocusArea));
    }
    if (storedAdvance.selectedValueCompass) {
      dispatch(setSelectedValueCompass(storedAdvance.selectedValueCompass));
    }
    if (storedAdvance.selectedMetrics) {
      dispatch(setSelectedMetrix(storedAdvance.selectedMetrics));
    }
    if (storedAdvance.selectedResults) {
      dispatch(setSelectedResults(storedAdvance.selectedResults));
    }
    if (storedAdvance.selectedTestOfChangeRating) {
      dispatch(setSelectedTestOfChangeRating(storedAdvance.selectedTestOfChangeRating));
    }
    if (storedAdvance.selectedProjectType) {
      dispatch(setSelectedProjectType(storedAdvance.selectedProjectType));
    }

    // Restore date/ROI fields into local state so they propagate to SearchProjectContainer
    if (storedAdvance.rangeStartDate) setRestoredRangeStartDate(storedAdvance.rangeStartDate);
    if (storedAdvance.rangeEndDate) setRestoredRangeEndDate(storedAdvance.rangeEndDate);
    if (storedAdvance.minROI) setRestoredMinROI(storedAdvance.minROI);
    if (storedAdvance.maxROI) setRestoredMaxROI(storedAdvance.maxROI);
  }, [dispatch]);

  /**
   * Builds the complete search params object from stored session data.
   */
  const buildRestoredSearchParams = useCallback((
    storedSearch: SharedSearchData,
    storedAdvance: AdvanceSearchData | null,
    restoredSearchBy: DropdownOption,
  ) => {
    // Use "~" separator to match the normal search flow in SearchProjectContainer,
    // which joins cost-centre codes with tilde before passing them to fetchProjectList.
    const costCenter = formatCostCenterForApi(storedSearch.selectedCostCenterCode, "~");
    const projectType = deriveProjectTypeForApi(storedAdvance?.selectedProjectType);

    return {
      region: getOptionCode(storedSearch.selectedRegionCode),
      entity: getOptionCode(storedSearch.selectedEntityCode),
      area: getOptionCode(storedSearch.selectedAreaCode),
      center: getOptionCode(storedSearch.selectedCenterCode),
      location: getOptionCode(storedSearch.selectedLocationCode),
      searchBy: restoredSearchBy.value,
      searchString: restoredSearchBy.value === storedSearch.selectedSearchBy?.value
        ? storedSearch.selectedSearchByText || undefined
        : "",
      fromId: restoredSearchBy.value === "UBTId" ? storedSearch.selectedFromId || undefined : undefined,
      toId: restoredSearchBy.value === "UBTId" ? storedSearch.selectedToId || undefined : undefined,
      costCenter,
      focusArea: formatMultiSelectForApi(storedAdvance?.selectedFocusArea),
      metric: formatMultiSelectForApi(storedAdvance?.selectedMetrics),
      valueCompassDesc: formatMultiSelectForApi(storedAdvance?.selectedValueCompass),
      result: formatMultiSelectForApi(storedAdvance?.selectedResults),
      rating: formatMultiSelectForApi(storedAdvance?.selectedTestOfChangeRating),
      projectType,
      sortBy: storedSearch.sortByCostCenter === "name" ? "DEPTNAME" : "DEPTID",
      sortColumn: SEARCH_PROJECTS_SORT_COLUMN_MAP["id"] ?? "ubtId",
      sortOrder: "ASC",
      page: 1,
      perPage: 20,
      startDate: storedAdvance?.rangeStartDate
        ? convertDateToApiFormat(storedAdvance.rangeStartDate)
        : undefined,
      endDate: storedAdvance?.rangeEndDate
        ? convertDateToApiFormat(storedAdvance.rangeEndDate)
        : undefined,
      minROI: storedAdvance?.minROI || undefined,
      maxROI: storedAdvance?.maxROI || undefined,
    };
  }, [formatCostCenterForApi, deriveProjectTypeForApi, convertDateToApiFormat]);

  /**
   * Helper to fetch dependent API data sequentially based on session filters
   */
  const fetchDependentData = useCallback(
    async (storedSearch: SharedSearchData) => {
      const regionCode = storedSearch.selectedRegionCode?.value;
      if (!regionCode || regionCode === "Select" || regionCode === "") return;

      const areaCode = storedSearch.selectedAreaCode?.value;
      const centerCode = storedSearch.selectedCenterCode?.value;
      const locationCode = storedSearch.selectedLocationCode?.value;
      const sortBy = storedSearch.sortByCostCenter ?? "id";

      try {
        // Fetch area options for the selected region
        if (regionCode) {
          await dispatch(
            fetchProjectArea({ regionCode, sortBy, isRestored: true }),
          ).unwrap();

          // If area exists and is not "All", fetch center options
          if (areaCode && areaCode !== "All") {
            await dispatch(
              fetchProjectCenter({ regionCode, areaCode, sortBy, isRestored: true }),
            ).unwrap();

            // If center exists and is not "All", fetch location options
            if (centerCode && centerCode !== "All") {
              await dispatch(
                fetchProjectLocation({
                  regionCode,
                  areaCode,
                  centerCode,
                  sortBy,
                  isRestored: true,
                }),
              ).unwrap();

              // If location exists and is not "All", fetch cost center options
              if (locationCode && locationCode !== "All") {
                await dispatch(
                  fetchProjectCostCenters({
                    regionCode,
                    areaCode,
                    centerCode,
                    locationCode,
                    sortBy,
                    isRestored: true,
                  }),
                ).unwrap();
              }
            }
          }
        }
      } catch {
        // Silently handle errors fetching dependent dropdown data;
        // the user can retry by changing their filter selection.
      }
    },
    [dispatch],
  );

  // Show error banner when the test-of-change export fails; auto-dismiss after 5 s
  useEffect(() => {
    if (tocExportError) {
      setShowTocExportErrorBanner(true);
      const timer = setTimeout(() => {
        setShowTocExportErrorBanner(false);
        dispatch(clearReportState());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [tocExportError, dispatch]);

  // Auto-dismiss export warning banner after 5 s and scroll to it
  useEffect(() => {
    if (!exportWarningMessage) return;
    exportWarningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timer = setTimeout(() => setExportWarningMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [exportWarningMessage]);

  // Preserve scroll position when loader appears (for dropdown changes, not search)
  useEffect(() => {
    if (loading || loadingDropdowns) {
      if (!isSearchScrolling.current) {
        savedScrollPosition.current = window.scrollY;
      }
    } else {
      // Restore scroll position after loading completes (only for non-search actions)
      if (savedScrollPosition.current > 0 && !isSearchScrolling.current) {
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollPosition.current);
          savedScrollPosition.current = 0;
        });
      }

      // If search just completed, scroll to table
      if (isSearchScrolling.current) {
        requestAnimationFrame(() => {
          scrollToTable();
          isSearchScrolling.current = false;
        });
      }
    }
  }, [loading, loadingDropdowns, scrollToTable]);

  useEffect(() => {
    // Wait for profile before initializing: profileRegionOption derives from profile
    // and creates a new object reference when profile loads (null → object), which
    // would cause this effect to run twice without the ref guard below.
    if (hasRunInitializationRef.current || !profile) return;
    hasRunInitializationRef.current = true;

    const defaultRegionCode = profileRegionOption.value;
    const defaultRegionDescription = profileRegionOption.label;

    // Load all dropdown options
    dispatch(fetchSearchByOptions());
    dispatch(fetchSearchProjectFocusData(defaultRegionCode));
    dispatch(fetchSearchProjectMetrics({ regionCode: defaultRegionCode }));
    dispatch(fetchSearchingClosedAndOpenedProjects());
    dispatch(fetchCostCenters());
    dispatch(fetchProjectRegion());

    // When session data exists, the session-restoration effect handles region
    // selection, dependent fetches, and the initial search with correct stored
    // values. Applying profile defaults here would overwrite persisted filters
    // and reset the cost-center selection (fetchProjectArea without isRestored).
    const hasSessionData = !!loadSearchFromSession();
    if (!hasSessionData) {
      dispatch(setSelectedRegionCode(profileRegionOption));
      dispatch(fetchProjectArea({ regionCode: defaultRegionCode, sortBy: "id" }));

      const defaultSearchParams = {
        region: defaultRegionDescription,
        sortOrder: "ASC",
        sortColumn: "id",
        entity: "All",
        area: "All",
        center: "All",
        location: "All",
        costCenter: "",
        focusArea: "All",
        metric: "All",
        valueCompassDesc: "All",
        result: "All",
        searchBy: "UBTName",
        sortBy: "DEPTID",
        projectType: "both",
        rating: "All",
        page: 1,
        perPage: 20,
      };

      dispatch(fetchProjectList(defaultSearchParams));
    }
  }, [dispatch, profile, profileRegionOption]);

  /**
   * Load search filters from sessionStorage and restore them.
   * sessionLoadInProgressRef prevents a second concurrent run when projectRegion
   * receives a new array reference (same data) before setSessionRestored(true)
   * has been processed — which would otherwise fire fetchProjectList twice.
   */
  useEffect(() => {
    if (sessionRestored || !projectRegion || projectRegion.length === 0) return;
    if (sessionLoadInProgressRef.current) return;

    const storedSearch = loadSearchFromSession();
    if (!storedSearch) {
      setSessionRestored(true);
      return;
    }

    sessionLoadInProgressRef.current = true;

    // Restore basic and advanced filters to Redux state
    const restoredSearchBy = resolveSearchByOption(
      storedSearch.selectedSearchBy,
      searchByOptions,
    );
    const restoreSearchByText =
      restoredSearchBy.value === storedSearch.selectedSearchBy?.value;
    restoreBasicSearchFilters(storedSearch, restoredSearchBy, restoreSearchByText);
    const storedAdvance = loadAdvanceSearch();
    restoreAdvancedSearchFilters(storedAdvance);

    // Fetch dependent dropdowns and execute search
    const loadData = async () => {
      await fetchDependentData(storedSearch);

      if (storedSearch.sortByCostCenter) {
        dispatch(setSortByCostCenter(storedSearch.sortByCostCenter));
      }
      if (storedSearch.selectedCostCenterCode) {
        dispatch(setSelectedCostCenterCode(storedSearch.selectedCostCenterCode));
      }

      setSessionRestored(true);

      const searchParams = buildRestoredSearchParams(
        storedSearch,
        storedAdvance,
        restoredSearchBy,
      );

      setLastSearchParams(searchParams);
      setShowResults(true);
      shouldScrollAfterSession.current = true;
      dispatch(fetchProjectList(searchParams));
    };

    loadData();
  }, [
    dispatch,
    projectRegion,
    sessionRestored,
    fetchDependentData,
    restoreBasicSearchFilters,
    restoreAdvancedSearchFilters,
    buildRestoredSearchParams,
    searchByOptions,
  ]);

  // Scroll to results table after session restoration auto-populates results
  useEffect(() => {
    if (shouldScrollAfterSession.current && !loading && showResults) {
      requestAnimationFrame(() => {
        if (!resultsTableRef.current) return;

        shouldScrollAfterSession.current = false;
        scrollToTable();
      });
    }
  }, [loading, showResults, scrollToTable]);

  const handleBreadcrumbPrevious = useCallback((): void => {
    navigate("/home");
  }, [navigate]);

  const handlePageChange = useCallback(
    (page: number) => {
      const params = {
        ...lastSearchParams,
        page,
        perPage,
        sortColumn: SEARCH_PROJECTS_SORT_COLUMN_MAP[sortColumn] ?? sortColumn,
        sortOrder: sortDirection,
      };
      dispatch(fetchProjectList(params));
    },
    [dispatch, perPage, sortColumn, sortDirection, lastSearchParams],
  );


  const valueCompassLookupMap = useMemo((): Map<number, string> => {
    const map = new Map<number, string>();
    (valueCompass ?? []).forEach((item: Record<string, unknown>) => {
      const id = Number(item.id);
      const label = item.label;
      if (!Number.isNaN(id) && (typeof label === "string" || typeof label === "number")) {
        map.set(id, String(label));
      }
    });
    return map;
  }, [valueCompass]);

  
  const resolveValueCompassLabel = useCallback(
    (valueCompassId: string | number | null | undefined): string => {
      if (!valueCompassId) return "";
      return String(valueCompassId)
        .split(",")
        .map((id) => valueCompassLookupMap.get(Number(id.trim())) ?? "")
        .filter(Boolean)
        .join(", ");
    },
    [valueCompassLookupMap],
  )
  /**
   * Adds a single-select field to params if it has a value.
   */
  const addSingleSelectParam = useCallback(
    (params: Record<string, string>, value: string | undefined, key: string) => {
      if (value) {
        params[key] = value;
      }
    },
    [],
  );

  /**
   * Adds a multi-select field to params (either "All" or comma-separated IDs).
   * For numeric/generic IDs — does NOT strip hyphens.
   * Use {@link addCostCenterParam} for cost-centre fields.
   * For costCenterCd, extracts the numeric part before the hyphen (e.g., "1235-med/al" => "1235")
   */
  const addMultiSelectParam = useCallback(
    (params: Record<string, string>, field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined, key: string) => {
      if (field?.isAllSelected) {
        params[key] = "All";
      } else if (field?.selectedIds?.length) {
        // Extract numeric part for costCenterCd, otherwise use as-is
        const ids = key === "costCenterCd"
          ? field.selectedIds.map((id: string | number) => String(id).split("-")[0]).filter(Boolean)
          : field.selectedIds;
        params[key] = ids.join(",");
      }
    },
    [],
  );

  /**
   * Adds a cost-centre field to params, stripping each label to its code prefix
   * (the part before the first hyphen, e.g. "5109-Local-Admin" → "5109").
   * @param separator - Delimiter between code values (default `","`).
   */
  const addCostCenterParam = useCallback(
    (
      params: Record<string, string>,
      field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined,
      key: string,
      separator: "," | "~" = ",",
    ) => {
      const value = getCostCenterParam(field, separator);
      if (value) {
        params[key] = value;
      }
    },
    [],
  );

  /**
   * Builds display summary and details report params from session storage.
   * Only includes params that have actual values. Uses helper functions to reduce complexity.
   */
  const buildDisplayReportParams = useCallback(
    (reportIdentifier: string, sortDirection: string = "asc"): Record<string, string> | null => {
      const searchData = loadSearchFromSession();
      const advanceData = loadAdvanceSearch();

      if (!searchData) {
        return null;
      }

      const params: Record<string, string> = {
        reportIdentifier,
        pageId: "ubtReportIdSearchProjects",
        sortColumn: "teamid",
        sortOrder: sortDirection.toLowerCase(),
      };

      // Single-select fields from basic search
      addSingleSelectParam(params, searchData.selectedRegionCode?.value, "regionCd");
      addSingleSelectParam(params, searchData.selectedEntityCode?.value, "entityCd");
      addSingleSelectParam(params, searchData.selectedAreaCode?.value, "areaCd");
      addSingleSelectParam(params, searchData.selectedCenterCode?.value, "centerCd");
      addSingleSelectParam(params, searchData.selectedLocationCode?.value, "locationCd");
      addSingleSelectParam(params, searchData.selectedSearchBy?.value, "queryBy");
      addSingleSelectParam(params, searchData.selectedSearchByText, "searchString");
      addSingleSelectParam(params, searchData.selectedProjectName, "searchProjectString");
      addUbtIdRange(params, searchData);


      // Cost-centre: strip label to code prefix, comma-separated
      addCostCenterParam(params, searchData.selectedCostCenterCode, "costCenterCd", ",");

      // Multi-select fields from advanced search
      addMultiSelectParam(params, advanceData?.selectedFocusArea, "focusAreaCd");
      addMultiSelectParam(params, advanceData?.selectedValueCompass, "valueCompassStaticCd");
      addMultiSelectParam(params, advanceData?.selectedMetrics, "metricCd");
      addMultiSelectParam(params, advanceData?.selectedResults, "resultCd");
      addMultiSelectParam(params, advanceData?.selectedTestOfChangeRating, "tocRatingCd");

      return params;
    },
    [addSingleSelectParam, addMultiSelectParam, sortDirection],
  );

  /**
   * Builds query parameters for the test-of-change CSV report from session storage.
   * Reuses the same param-building helpers as `buildDisplayReportParams` so all
   * filter fields are mapped consistently.
   *
   * Sort params (`sortColumn` / `sortOrder`) are hardcoded to match the canonical
   * report API contract (`ubtId` / `asc`) and are intentionally decoupled from the
   * table's current display sort state.
   *
   * `includeProjectType` is always included, defaulting to "active" when no project
   * type has been explicitly selected.
   *
   * @returns Typed params object ready for `exportTestOfChangeCSV`, or null when
   *          no session search data is available.
   */
  const buildTestOfChangeReportParams = useCallback(
    (): TestOfChangeReportParams | null => buildTestOfChangeParamsFromSession(sortDirection),
    [sortDirection],
  );

  /**
   * Builds query parameters for the `/ubt/reports/test-series` API from session
   * storage (SearchUBTProjects page).
   */
  const buildTestSeriesParams = useCallback(
    (): Record<string, string> | null => buildTestSeriesParamsFromSession(sortDirection),
    [sortDirection],
  );

  /**
   * Builds PIP History report params from session storage for searchProject mode.
   * Includes ALL params (defaulting to "All") to ensure the backend receives complete filter context.
   */
  const buildPipHistoryParams = useCallback((): Record<string, string> => {
    const searchData = loadSearchFromSession();
    const advanceData = loadAdvanceSearch();

    const extractCode = (value: string | undefined): string => {
      if (!value || value === "Select") return "All";
      return value.split("~")[0] || "All";
    };

    const getMultiSelectValue = (field: { isAllSelected: boolean; selectedIds: (string | number)[] } | undefined): string => {
      const formatted = formatMultiSelectForApi(field as any);
      return formatted || "All";
    };

    // Helper: Extract codes from multi-select with descriptions (e.g., "0101-Med/Surg~0102-ICU" -> "0101~0102")
    const extractCodesFromMultiSelect = (field: any): string => {
      if (!field || field.isAllSelected) return "All";
      if (!field.selectedIds || field.selectedIds.length === 0) return "All";
      return field.selectedIds
        .map((id: string | number) => String(id).split("-")[0].trim())
        .join("~");
    };

    // Single-select fields (default to "All" if not selected)
    const region = extractCode(searchData?.selectedRegionCode?.value);
    const entity = extractCode(searchData?.selectedEntityCode?.value);
    const area = extractCode(searchData?.selectedAreaCode?.value);
    const center = extractCode(searchData?.selectedCenterCode?.value);
    const location = extractCode(searchData?.selectedLocationCode?.value);

    // Multi-select fields (use formatMultiSelectForApi which returns "All" or tilde-delimited IDs)
    // For costCenter, extract only codes without descriptions (e.g., "0101~0102" not "0101-Med/Surg~0102-ICU")
    const costCenter = extractCodesFromMultiSelect(searchData?.selectedCostCenterCode);
    const focusArea = getMultiSelectValue(advanceData?.selectedFocusArea);
    const metric = getMultiSelectValue(advanceData?.selectedMetrics);
    const valueCompass = getMultiSelectValue(advanceData?.selectedValueCompass);
    const result = getMultiSelectValue(advanceData?.selectedResults);
    const rating = getMultiSelectValue(advanceData?.selectedTestOfChangeRating);

    const searchBy = searchData?.selectedSearchBy?.value || "UBTName";
    const searchString = searchData?.selectedSearchByText || "";
    // Only carry a value when the user searched by project name; the slice's
    // buildPipHistoryQueryParams drops empty values, so this keeps the param
    // out of the URL unless a project name is present.
    const searchByProject = searchData?.selectedProjectName ?? "";

    const projectTypeMapping: Record<string, string> = {
      active: "Active",
      complete: "Complete",
      closed: "Complete",
      both: "Both",
      all: "Both",
    };
    const projectType = advanceData?.selectedProjectType;
    const normalizedType = (typeof projectType === "string" ? projectType : "active").toLowerCase();
    const perfStatus = projectTypeMapping[normalizedType] || "Active";

    return {
      region,
      area,
      center,
      location,
      entity,
      costCenter,
      focusArea,
      rating,
      searchBy,
      perfStatus,
      metric,
      valueCompass,
      result,
      searchString,
      searchByProject,
      ...(searchData?.selectedSearchBy?.value === "UBTId"
        ? {
            fromId: searchData.selectedFromId ?? "",
            toId: searchData.selectedToId ?? "",
          }
        : {}),
      sortOrder: "ASC",
      sortColumn: "TEAMID",
    };
  }, []);

  /** Dispatches the Test-of-Change CSV export. */
  const handleTocExport = useCallback(() => {
    const tocParams = buildTestOfChangeReportParams();
    if (tocParams) dispatch(exportTestOfChangeCSV(tocParams));
  }, [buildTestOfChangeReportParams, dispatch]);

  /** Dispatches the Test-Series CSV export (requires nuid). */
  const handleTestSeriesExport = useCallback(() => {
    const nuid = String(profile?.nuid ?? "");
    const testSeriesParams = buildTestSeriesParams();
    if (nuid && testSeriesParams) {
      dispatch(downloadTestSeriesByCriteriaReport({ nuid, params: testSeriesParams }));
    }
  }, [profile, buildTestSeriesParams, dispatch]);

  /** Dispatches the PIP History CSV export (requires nuid). */
  const handlePipHistoryExport = useCallback(() => {
    const nuid = String(profile?.nuid ?? "");
    if (nuid) dispatch(downloadPipHistoryReport({ nuid, pageId: "searchProject", ...buildPipHistoryParams() }));
  }, [profile, buildPipHistoryParams, dispatch]);

  /** Dispatches the PIP Details CSV export (requires nuid). */
  const handlePipDetailsExport = useCallback(() => {
    const nuid = String(profile?.nuid ?? "");
    if (nuid) {
      const pipDetailsParams = buildPipDetailsParams("TEAMID", sortDirection);
      dispatch(downloadPipDetailsReport({ nuid, pageId: "", ...pipDetailsParams }));
    }
  }, [profile, sortDirection, dispatch]);

  /**
   * Handles report export for search projects.
   * Reads all filter parameters from session storage and dispatches the
   * appropriate download thunk based on the selected report option.
   */
  const handleReportExport = useCallback(
    (selectedReport: DropdownOption) => {
      const isTocReport =
        selectedReport?.id === EXPORT_TEST_OF_CHANGE_ID &&
        selectedReport?.value === EXPORT_TEST_OF_CHANGE_VALUE;

      if (isTocReport) { handleTocExport(); return; }
      if (selectedReport?.id === EXPORT_TEST_DATA_OPTION_ID) { handleTestSeriesExport(); return; }
      if (selectedReport?.id === EXPORT_PIP_HISTORY_OPTION_ID) { handlePipHistoryExport(); return; }
      if (selectedReport?.id === EXPORT_PIP_DETAILS_OPTION_ID) { handlePipDetailsExport(); return; }

      const warning = getTeamReportWarning(selectedReport?.id, totalItems);
      setExportWarningMessage(warning);
      if (warning) return;

      const reportIdentifier = selectedReport?.id === "team_details" ? "teamdetails" : "teamsummary";
      const params = buildDisplayReportParams(reportIdentifier, sortDirection);
      if (params) dispatch(downloadDisplaySummaryAndDetailsReport(params));
    },
    [dispatch, buildDisplayReportParams, handleTocExport, handleTestSeriesExport, handlePipHistoryExport, handlePipDetailsExport, sortDirection, totalItems],
  );

  /**
   * Expands the team-grouped `projectList` (already grouped in the Redux slice)
   * into one table row per project. Team ID and UBT name appear only on the
   * first row of each group — mirroring the Find & Select UBT rendering pattern.
   *
   * `rowKey` uses `teamIndex` (position in the array) to guarantee uniqueness
   * even when teams share an empty or duplicate `id`.
   */
  const rows: TableRow[] = useMemo(() => {
    return (projectList).flatMap(
      (team: GroupedProject, teamIndex: number) => {
        if (!team.projects || team.projects.length === 0) {
          return [
            {
              rowKey: `team-${teamIndex}-0`,
              id: team.id,
              idOrig: "",
              pipId: "",
              UBTname: (
                <span
                  className="whitespace-normal wrap-break-word"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {team.UBTname}
                </span>
              ),
              centerName: "",
              locationName: "",
              projectName: "",
              focusArea: "",
              results: "",
              valueCompass: "",
              projectStartDate: "",
            },
          ];
        }

        return team.projects.map((project: ProjectDetail, index: number) => ({
          rowKey: `team-${teamIndex}-${index}`,
          // Show team ID and UBT name only on the first project row of the group.
          id: index === 0 ? team.id : "",
          idOrig: project.idOrig ?? "",
          pipId: project.pipId ?? "",
          UBTname:
            index === 0 ? (
              <span
                className="whitespace-normal wrap-break-word"
                style={{ overflowWrap: "anywhere" }}
              >
                {team.UBTname}
              </span>
            ) : (
              ""
            ),
          centerName: project.centerName ?? "",
          locationName: project.locationName ?? "",
          projectName: project.projectName ?? "",
          focusArea: project.focusArea ?? "",
          results: project.results ?? "",
          valueCompass: resolveValueCompassLabel(project.valueCompassId),
          projectStartDate: project.projectStartDate ?? "",
        }));
      },
    );
  }, [projectList, resolveValueCompassLabel]);

  const handleSort = useCallback(
    (columnKey: string, direction: "ASC" | "DESC") => {
      const apiSortColumn = SEARCH_PROJECTS_SORT_COLUMN_MAP[columnKey] ?? columnKey;
      setSortColumn(columnKey);
      setSortDirection(direction);
      const param = {
        ...lastSearchParams,
        page: currentPage,
        perPage,
        sortColumn: apiSortColumn,
        sortOrder: direction,
      };
      dispatch(fetchProjectList(param));
    },
    [dispatch, currentPage, perPage, lastSearchParams],
  );

  const handleSearch = useCallback(
    (searchParams: Record<string, unknown>) => {
      setShowResults(true);
      isSearchScrolling.current = true;
      // Reset sort direction to ASC for new searches
      setSortColumn("id");
      setSortDirection("ASC");
      const params = {
        page: 1,
        perPage,
        ...searchParams,
      };
      setLastSearchParams(params);
      
      // Save basic search filters to ubt-shared-search
      const searchData = {
        selectedRegionCode: effectiveSelectedRegionCode,
        selectedEntityCode,
        selectedAreaCode,
        selectedCenterCode,
        selectedLocationCode,
        selectedCostCenterCode,
        sortByCostCenter,
        selectedSearchBy,
        selectedSearchByText,
        selectedFromId,
        selectedToId,
        selectedProjectName,
      };
      saveSearchToSession(searchData);

      // Save advanced search filters to ubt-advance-search.
      // Date values come through as rangeStartDateRaw / rangeEndDateRaw (MM-DD-YYYY)
      // so they can be restored to the date-picker display format.
      const advanceData: AdvanceSearchData = {
        selectedFocusArea: selectedRating,
        selectedValueCompass,
        selectedMetrics: selectedMetrix,
        selectedResults,
        selectedTestOfChangeRating,
        selectedProjectType,
        rangeStartDate: typeof searchParams.rangeStartDateRaw === "string" ? searchParams.rangeStartDateRaw : "",
        rangeEndDate: typeof searchParams.rangeEndDateRaw === "string" ? searchParams.rangeEndDateRaw : "",
        minROI: typeof searchParams.minROI === "string" ? searchParams.minROI : "",
        maxROI: typeof searchParams.maxROI === "string" ? searchParams.maxROI : "",
      };
      saveAdvanceSearch(advanceData);
      
      dispatch(fetchProjectList(params));
    },
    [
      dispatch,
      perPage,
      effectiveSelectedRegionCode,
      selectedEntityCode,
      selectedAreaCode,
      selectedCenterCode,
      selectedLocationCode,
      selectedCostCenterCode,
      sortByCostCenter,
      selectedRating,
      selectedValueCompass,
      selectedMetrix,
      selectedResults,
      selectedTestOfChangeRating,
      selectedProjectType,
      selectedSearchBy,
      selectedSearchByText,
      selectedFromId,
      selectedToId,
      selectedProjectName,
    ],
  );

  const handleProjectNameClick = useCallback(
    (rowData: TableRow) => {
      const projectId = rowData.idOrig;
      const pipId = rowData.pipId;
      if (
        projectId &&
        (typeof projectId === "string" || typeof projectId === "number")
      ) {
        teamStorage.setTeamId(projectId);
        // Always clear any stale pipId first, then set only when a valid value exists
        sessionStorage.removeItem("selectedPipId");
        if (pipId != null && pipId !== "") {
          sessionStorage.setItem("selectedPipId", String(pipId as string | number));
        }
        navigate("/home/projects", {
          state: {
            teamId: projectId,
            pipId,
            from: "Search Projects",
          },
        });
      }
    },
    [navigate],
  );

  const handleRegionChange = useCallback(
    (regionOption: any) => {
      dispatch(
        setSelectedRegionCode(
          regionOption ?? { id: "", label: "Select", value: "Select" },
        ),
      );
      dispatch(resetDependentDropdowns({ level: "region" }));
      if (regionOption?.value && regionOption.value !== "Select") {
        dispatch(fetchProjectArea({ regionCode: regionOption.value }));
        // Call new APIs with updated region code
        dispatch(fetchSearchProjectFocusData(regionOption.value));
        dispatch(fetchSearchProjectMetrics({ regionCode: regionOption.value }));
      } else {
        console.error("fetchProjectArea NOT called - invalid regionOption");
      }
    },
    [dispatch],
  );

  const handleEntityChange = useCallback(
    (entityOption: any) => {
      dispatch(
        setSelectedEntityCode(
          entityOption ?? { id: 0, label: "All", value: "All" },
        ),
      );
    },
    [dispatch],
  );

  const handleAreaChange = useCallback(
    (areaOption: any) => {
      dispatch(
        setSelectedAreaCode(
          areaOption ?? { id: 0, label: "All", value: "All" },
        ),
      );
      dispatch(resetDependentDropdowns({ level: "area" }));
      if (
        areaOption?.value &&
        areaOption.value !== "All" &&
        selectedRegionCode?.value
      ) {
        dispatch(
          fetchProjectCenter({
            regionCode: selectedRegionCode.value,
            areaCode: areaOption.value,
          }),
        );
      }
    },
    [dispatch, selectedRegionCode],
  );

  const handleCenterChange = useCallback(
    (centerOption: any) => {
      dispatch(
        setSelectedCenterCode(
          centerOption ?? { id: 0, label: "All", value: "All" },
        ),
      );
      dispatch(resetDependentDropdowns({ level: "center" }));
      if (
        centerOption?.value &&
        centerOption.value !== "All" &&
        selectedRegionCode?.value &&
        selectedAreaCode?.value &&
        selectedAreaCode.value !== "All"
      ) {
        dispatch(
          fetchProjectLocation({
            regionCode: selectedRegionCode.value,
            areaCode: selectedAreaCode.value,
            centerCode: centerOption.value,
          }),
        );
      }
    },
    [dispatch, selectedRegionCode, selectedAreaCode],
  );

  const handleLocationChange = useCallback(
    (locationOption: any) => {
      dispatch(
        setSelectedLocationCode(
          locationOption ?? { id: 0, label: "All", value: "All" },
        ),
      );
      dispatch(resetDependentDropdowns({ level: "location" }));
      if (
        locationOption?.value &&
        locationOption.value !== "All" &&
        selectedRegionCode?.value &&
        selectedAreaCode?.value &&
        selectedCenterCode?.value &&
        selectedAreaCode.value !== "All" &&
        selectedCenterCode.value !== "All"
      ) {
        dispatch(
          fetchProjectCostCenters({
            regionCode: selectedRegionCode.value,
            areaCode: selectedAreaCode.value,
            centerCode: selectedCenterCode.value,
            locationCode: locationOption.value,
            sortBy: sortByCostCenter,
          }),
        );
      }
    },
    [
      dispatch,
      selectedRegionCode,
      selectedAreaCode,
      selectedCenterCode,
      sortByCostCenter,
    ],
  );

  const handleCostCenterChange = useCallback(
    (selectedIds: (string | number)[], isAllSelected: boolean) => {
      dispatch(setSelectedCostCenterCode({ selectedIds, isAllSelected }));
    },
    [dispatch],
  );

  const handleCostCenterSortBy = useCallback(
    (sortBy: string) => {
      dispatch(setSortByCostCenter(sortBy));

      // Call the appropriate API based on the current selection level
      if (
        selectedRegionCode?.value &&
        selectedAreaCode?.value &&
        selectedCenterCode?.value &&
        selectedLocationCode?.value &&
        selectedAreaCode.value !== "All" &&
        selectedCenterCode.value !== "All" &&
        selectedLocationCode.value !== "All"
      ) {
        // Location is selected - call fetchProjectCostCenters
        dispatch(
          fetchProjectCostCenters({
            regionCode: selectedRegionCode.value,
            areaCode: selectedAreaCode.value,
            centerCode: selectedCenterCode.value,
            locationCode: selectedLocationCode.value,
            sortBy,
          }),
        );
      } else if (
        selectedRegionCode?.value &&
        selectedAreaCode?.value &&
        selectedCenterCode?.value &&
        selectedAreaCode.value !== "All" &&
        selectedCenterCode.value !== "All"
      ) {
        // Center is selected but Location is "All" - call fetchProjectLocation
        dispatch(
          fetchProjectLocation({
            regionCode: selectedRegionCode.value,
            areaCode: selectedAreaCode.value,
            centerCode: selectedCenterCode.value,
            sortBy,
          }),
        );
      } else if (
        selectedRegionCode?.value &&
        selectedAreaCode?.value &&
        selectedAreaCode.value !== "All"
      ) {
        // Area is selected but Center is "All" - call fetchProjectCenter
        dispatch(
          fetchProjectCenter({
            regionCode: selectedRegionCode.value,
            areaCode: selectedAreaCode.value,
            sortBy,
          }),
        );
      } else if (selectedRegionCode?.value) {
        // Only Region is selected - call fetchProjectArea
        dispatch(
          fetchProjectArea({
            regionCode: selectedRegionCode.value,
            sortBy,
          }),
        );
      }
    },
    [
      dispatch,
      selectedRegionCode,
      selectedAreaCode,
      selectedCenterCode,
      selectedLocationCode,
    ],
  );

  const handleRatingChange = useCallback(
    (selectedIds: (string | number)[], isAllSelected: boolean) => {
      dispatch(setSelectedRating({ selectedIds, isAllSelected }));

      // Call metrics API with focusIds when Focus Area selection changes
      const regionCode = selectedRegionCode?.value || "02";

      // Create a unique key for this API call to prevent duplicates
      let callKey: string;
      if (isAllSelected) {
        callKey = `${regionCode}-all`;
      } else {
        // Filter out 0 and invalid IDs before sending
        const validIds = selectedIds.filter(
          (id) => id && id !== 0 && id !== "0",
        );
        const sortedIds = [...validIds].sort((a, b) => Number(a) - Number(b));
        callKey = `${regionCode}-${sortedIds.join(",")}`;
      }

      // Only call API if parameters have changed
      if (lastFocusAreaCallRef.current === callKey) {
        return;
      }
      lastFocusAreaCallRef.current = callKey;

      if (isAllSelected) {
        // When "All" is selected, don't send focusId parameter
        dispatch(fetchSearchProjectMetrics({ regionCode }));
      } else {
        // Filter out 0 and invalid IDs before sending
        const validIds = selectedIds.filter(
          (id) => id && id !== 0 && id !== "0",
        );

        if (validIds.length > 0) {
          // When specific items are selected, send comma-separated focusIds
          const focusIds = validIds.join(",");
          dispatch(fetchSearchProjectMetrics({ regionCode, focusIds }));
        } else {
          // When no valid items selected, call without focusIds
          dispatch(fetchSearchProjectMetrics({ regionCode }));
        }
      }
    },
    [dispatch, selectedRegionCode],
  );

  const handleValueCompassChange = useCallback(
    (selectedIds: (string | number)[], isAllSelected: boolean) => {
      dispatch(setSelectedValueCompass({ selectedIds, isAllSelected }));
    },
    [dispatch],
  );

  const handleMetrixChange = useCallback(
    (selectedIds: (string | number)[], isAllSelected: boolean) => {
      dispatch(setSelectedMetrix({ selectedIds, isAllSelected }));
    },
    [dispatch],
  );

  const handleResultsChange = useCallback(
    (selectedIds: (string | number)[], isAllSelected: boolean) => {
      dispatch(setSelectedResults({ selectedIds, isAllSelected }));
    },
    [dispatch],
  );

  const handleTestOfChangeRatingChange = useCallback(
    (selectedIds: (string | number)[], isAllSelected: boolean) => {
      dispatch(setSelectedTestOfChangeRating({ selectedIds, isAllSelected }));
    },
    [dispatch],
  );

  const handleProjectTypeChange = useCallback(
    (value: string) => {
      dispatch(setSelectedProjectType(value));
    },
    [dispatch],
  );

  const handleSearchByChange = useCallback(
    (option: any) => {
      dispatch(setSelectedSearchBy(option ?? { id: 0, label: "UBT Name", value: "UBTName" }));
      dispatch(setSelectedSearchByText(""));
      if (option?.value !== "UBTId") {
        dispatch(setSelectedFromId(""));
        dispatch(setSelectedToId(""));
      }
    },
    [dispatch],
  );

  const handleSearchByTextChange = useCallback(
    (text: string) => {
      dispatch(setSelectedSearchByText(text));
    },
    [dispatch],
  );

  const handleFromIdChange = useCallback((value: string) => {
    dispatch(setSelectedFromId(value));
  }, [dispatch]);

  const handleToIdChange = useCallback((value: string) => {
    dispatch(setSelectedToId(value));
  }, [dispatch]);

  const handleProjectNameChange = useCallback(
    (text: string) => {
      dispatch(setSelectedProjectName(text));
    },
    [dispatch],
  );

  return (
    <>
      {(loading || loadingDropdowns || (isPageInitializing && !!profile)) && <Loader />}
      <div className="min-h-[70vh] lg:mx-40! mx-5!">
        <div className="flex justify-between items-end">
          <div>
            <BreadCrumbs
              previousPage="UBT Home"
              currentPage="Search Projects"
              onPreviousClick={handleBreadcrumbPrevious}
            />
          </div>

          <div className="mobile-margin lg:mt-15!">
            <Link
              to="/home/my-teams"
              data-analytics-type="link"
              state={{ from: "Search Projects" } }
              className="no-underline! p1 -medium"
            >
              My Teams
            </Link>
            <Link
              to="/home/select-ubt"
              data-analytics-type="link"
              state={{ from: "Search Projects" }}
              className="no-underline! p1 -medium pl-6!"
            >
              Find and Select a UBT
            </Link>
            <Link
              to="/home/teams-reporting"
              data-analytics-type="link"
              state={{ from: "Search Projects" }}
              className="no-underline! p1 -medium pl-6!"
            >
              Teams Reporting
            </Link>
          </div>
        </div>

        <div className="flex justify-between items-end mb-18! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
          <div className="">
            <h1 className="h1 tablet:mt-8! mobile:mt-3.5! mb-0!">
              Search Projects
            </h1>
          </div>
        </div>

        <div className="bg-white border-solid border-2 border-border-light w-8xl rounded-xl py-8! pl-8! pr-0! mt-8! mb-24!">
          <div className="flex gap-x-1 mb-8!">
            <h3 className="h3 m-0!">Search performance improvement projects</h3>
            <PopUp
            className="ml-1! -medium"
            headerText="Search Projects Page Help"
            message={`View the <a href="#" data-open-modal="search-projects-help" rel="noopener noreferrer"> additional page help. </a><br/><br/>
              View the <a target="_blank" rel="noopener noreferrer" href="https://www.lmpartnership.org/path-to-performance/ubt-basics">the National LMP web site. </a>`}
          />
          </div>

          <SearchProjectContainer
            regionOptions={projectRegion ?? []}
            entityOptions={projectEntity ?? []}
            areaOptions={projectArea ?? []}
            centerOptions={projectCenter ?? []}
            locationOptions={projectLocation ?? []}
            costCenterOptions={costCenters}
            ratingOptions={ratingOption}
            rating={rating}
            results={results}
            valueCompass={valueCompass}
            testOfChangeRating={testOfChangeRating}
            searchByOptions={searchByOptions ?? []}
            onSearch={handleSearch}
            onRegionChange={handleRegionChange}
            onEntityChange={handleEntityChange}
            onAreaChange={handleAreaChange}
            onCenterChange={handleCenterChange}
            onLocationChange={handleLocationChange}
            onCostCenterChange={handleCostCenterChange}
            onCostCenterSortBy={handleCostCenterSortBy}
            onRatingChange={handleRatingChange}
            onValueCompassChange={handleValueCompassChange}
            onMetrixChange={handleMetrixChange}
            onResultsChange={handleResultsChange}
            onTestOfChangeRatingChange={handleTestOfChangeRatingChange}
            onProjectTypeChange={handleProjectTypeChange}
            onSearchByChange={handleSearchByChange}
            onSearchByTextChange={handleSearchByTextChange}
            onFromIdChange={handleFromIdChange}
            onToIdChange={handleToIdChange}
            onProjectNameChange={handleProjectNameChange}
            selectedRegionCode={effectiveSelectedRegionCode}
            selectedEntityCode={selectedEntityCode}
            selectedAreaCode={selectedAreaCode}
            selectedCenterCode={selectedCenterCode}
            selectedLocationCode={selectedLocationCode}
            selectedCostCenterCode={selectedCostCenterCode}
            selectedRating={selectedRating}
            selectedValueCompass={selectedValueCompass}
            selectedMetrix={selectedMetrix}
            selectedResults={selectedResults}
            selectedTestOfChangeRating={selectedTestOfChangeRating}
            selectedProjectType={selectedProjectType}
            selectedSearchBy={selectedSearchBy}
            selectedSearchByText={selectedSearchByText}
            selectedFromId={selectedFromId}
            selectedToId={selectedToId}
            selectedProjectName={selectedProjectName}
            sortByCostCenter={sortByCostCenter}
            isLoadingData={loadingDropdowns}
            rangeStartDate={restoredRangeStartDate}
            rangeEndDate={restoredRangeEndDate}
            minROI={restoredMinROI}
            maxROI={restoredMaxROI}
          />
        </div>

        {exportWarningMessage && (
          <div ref={exportWarningRef}>
             <CommonErrorBanner
            variant="Alert"
            heading=""
            messageInfo={exportWarningMessage}
          />
    
          </div>
        )}

        {showTocExportErrorBanner && tocExportError && (
          <div
            role="alert"
            aria-live="assertive"
            className="ds-banner ds-banner__bg--white ds-banner--icon !mb-[24px]"
            style={{ border: '1px solid var(--Color-Red, #C40000)', padding: '1px' }}
          >
            <div className="ds-banner__icon-img">
              <img
                src="/assets/images/alertSolid.svg"
                alt=""
                style={{ width: '38px', height: '38px' }}
              />
            </div>
            <div className="ds-banner__container">
              <div className="ds-banner__main flex flex-col">
                <div className="ds-banner__main-body p1 -medium" style={{ color: 'var(--Color-Inky, #0D1C3D)', marginTop: '7px' }}>
                  {tocExportError}
                </div>
              </div>
            </div>
          </div>
        )}

        {showResults && (
          <div ref={resultsTableRef} className="relative">
            <UBTSearchResults
              rows={rows}
              columns={columns}
              totalItems={totalItems || projectList.length}
              itemsPerPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onSort={handleSort}
              onActionClick={handleProjectNameClick}
              actionKey="projectName"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              teamsCount={totalItems || projectList.length}
              teamsCountMessage="Projects Found"
              isLoading={loading}
              reportOptions={getSearchUBTProjectsOptions()}
              onReportSelect={handleReportExport}
            />
            {(pdfDownloadLoading || tocExportLoading || pipHistoryLoading) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded">
                <Loader />
              </div>
            )}
          </div>
        )}

        <div className="mb-10!">
          <div className="-mb-27! tablet:hidden">
            <ScrollToTopBtn />
          </div>
        </div>
      </div>

      <SearchProjectsHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </>
  );
};

export default SearchProject;
